import React from 'react';
import Protect from '../auth/Protect';
import { getPermissionForNavigation } from '@/utils/betterAuthPermissionMapper';

interface CanPerformActionProps {
    action: string;
    conditions?: any;
    children: React.ReactNode;
    fallback?: React.ReactNode;
    onUnauthorized?: () => void;
}

/**
 * Component that conditionally renders children based on action permissions
 * Uses the Better Auth Protect component instead of CASL
 */
export const CanPerformAction: React.FC<CanPerformActionProps> = ({
    action,
    conditions,
    children,
    fallback = null,
    onUnauthorized
}) => {
    const clerkPermission = getPermissionForNavigation(action);

    React.useEffect(() => {
        // Note: Better Auth's Protect handles unauthorized access automatically
        // This is kept for backward compatibility
        if (onUnauthorized) {
            // We can't easily detect unauthorized state with Protect
            // This callback may not work as expected
        }
    }, [onUnauthorized]);

    return (
        <Protect permission={clerkPermission} fallback={fallback}>
            {children}
        </Protect>
    );
};

/**
 * Higher-order component for action-based permissions
 * Uses the Better Auth Protect component
 */
export function withActionPermission<P extends object>(
    Component: React.ComponentType<P>,
    action: string,
    conditions?: any
) {
    return function PermissionWrappedComponent(props: P) {
        const clerkPermission = getPermissionForNavigation(action);

        return (
            <Protect permission={clerkPermission}>
                <Component {...props} />
            </Protect>
        );
    };
} 
